import { ScheduleEntry, GapRule, Preset, PresetGroup } from './supabase';

interface DoseItem {
  scheduleEntryId: string;
  presetSnapshot: {
    name: string;
    tag: string;
    priority: number;
    food_relation: string;
    description?: string;
  };
  desiredTime: Date;
  plannedTime: Date;
  conflictNotes: string[];
}

function expandGapRules(gapRules: GapRule[]): Array<{ source_tag: string; target_tag: string; min_gap_min: number }> {
  const expanded: Array<{ source_tag: string; target_tag: string; min_gap_min: number }> = [];

  for (const rule of gapRules) {
    const sourceTags: string[] = [];
    const targetTags: string[] = [];

    if (rule.source_tag) {
      sourceTags.push(rule.source_tag);
    } else if (rule.source_group && rule.source_group.presets) {
      sourceTags.push(...rule.source_group.presets.map(p => p.tag));
    }

    if (rule.target_tag) {
      targetTags.push(rule.target_tag);
    } else if (rule.target_group && rule.target_group.presets) {
      targetTags.push(...rule.target_group.presets.map(p => p.tag));
    }

    for (const sourceTag of sourceTags) {
      for (const targetTag of targetTags) {
        expanded.push({
          source_tag: sourceTag,
          target_tag: targetTag,
          min_gap_min: rule.min_gap_min,
        });
      }
    }
  }

  return expanded;
}

export function generateDayPlan(
  wakeTime: Date,
  entries: (ScheduleEntry & { preset: Preset })[],
  gapRules: GapRule[]
): DoseItem[] {
  const expandedRules = expandGapRules(gapRules);
  const items: DoseItem[] = entries.map(entry => {
    const desiredTime = new Date(wakeTime.getTime() + entry.desired_offset_min * 60000);
    return {
      scheduleEntryId: entry.id,
      presetSnapshot: {
        name: entry.preset.name,
        tag: entry.preset.tag,
        priority: entry.preset.priority,
        food_relation: entry.preset.food_relation,
        description: entry.preset.description || undefined,
      },
      desiredTime,
      plannedTime: new Date(desiredTime),
      conflictNotes: [],
    };
  });

  items.sort((a, b) => {
    if (a.presetSnapshot.priority !== b.presetSnapshot.priority) {
      return a.presetSnapshot.priority - b.presetSnapshot.priority;
    }
    return a.desiredTime.getTime() - b.desiredTime.getTime();
  });

  const maxIterations = 10;
  let iteration = 0;
  let changed = true;

  while (changed && iteration < maxIterations) {
    changed = false;
    iteration++;

    for (let i = 0; i < items.length; i++) {
      const currentItem = items[i];
      const applicableRules = expandedRules.filter(
        rule => rule.source_tag === currentItem.presetSnapshot.tag
      );

      for (const rule of applicableRules) {
        for (let j = 0; j < i; j++) {
          const targetItem = items[j];
          if (targetItem.presetSnapshot.tag === rule.target_tag) {
            const requiredTime = new Date(
              targetItem.plannedTime.getTime() + rule.min_gap_min * 60000
            );

            if (currentItem.plannedTime < requiredTime) {
              const oldTime = new Date(currentItem.plannedTime);
              currentItem.plannedTime = requiredTime;
              changed = true;

              const shiftMinutes = Math.round(
                (requiredTime.getTime() - oldTime.getTime()) / 60000
              );

              const note = `+${shiftMinutes}m to stay ${rule.min_gap_min}m after ${targetItem.presetSnapshot.name} (tag: ${rule.target_tag})`;

              if (!currentItem.conflictNotes.some(n => n.includes(rule.target_tag))) {
                currentItem.conflictNotes.push(note);
              }
            }
          }
        }
      }
    }
  }

  return items;
}

export function reflowAfterChange(
  items: DoseItem[],
  changedIndex: number,
  gapRules: GapRule[]
): DoseItem[] {
  const expandedRules = expandGapRules(gapRules);
  const result = [...items];

  for (let i = changedIndex + 1; i < result.length; i++) {
    const currentItem = result[i];
    const applicableRules = expandedRules.filter(
      rule => rule.source_tag === currentItem.presetSnapshot.tag
    );

    for (const rule of applicableRules) {
      for (let j = 0; j < i; j++) {
        const targetItem = result[j];
        if (targetItem.presetSnapshot.tag === rule.target_tag) {
          const requiredTime = new Date(
            targetItem.plannedTime.getTime() + rule.min_gap_min * 60000
          );

          if (currentItem.plannedTime < requiredTime) {
            currentItem.plannedTime = requiredTime;
            const shiftMinutes = Math.round(
              (requiredTime.getTime() - currentItem.desiredTime.getTime()) / 60000
            );

            const note = `+${shiftMinutes}m to stay ${rule.min_gap_min}m after ${targetItem.presetSnapshot.name} (tag: ${rule.target_tag})`;

            currentItem.conflictNotes = currentItem.conflictNotes.filter(
              n => !n.includes(rule.target_tag)
            );
            currentItem.conflictNotes.push(note);
          }
        }
      }
    }
  }

  return result;
}

export function formatTime(date: Date): string {
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

export function formatFoodRelation(relation: string): string {
  switch (relation) {
    case 'ANY':
      return 'Any time';
    case 'BEFORE_30M':
      return '30m before food';
    case 'WITH_MEAL':
      return 'With meal';
    case 'AWAY_120M':
      return '2h away from food';
    default:
      return relation;
  }
}

export function isNextDay(wakeTime: Date, plannedTime: Date): boolean {
  const wakeDay = wakeTime.getDate();
  const plannedDay = plannedTime.getDate();
  return plannedDay !== wakeDay || plannedTime < wakeTime;
}
